import React, { useEffect, useState, useRef } from 'react';
import { changeLanguage } from '@utils/i18n';
import { useTranslation } from 'react-i18next';

import { useSelector, useDispatch } from 'react-redux';
import { setLang } from '@modules/ui';
import Modal from '@mui/material/Modal';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import LanguageIcon from '@mui/icons-material/Language';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';

function LangSelectModal({ isOpen, toggleModal }) {
    const { i18n, t } = useTranslation();
    const dispatch = useDispatch();
    const [searchTerm, setSearchTerm] = useState('');
    const selectedLangRef = useRef(null);
    const [selectedLanguage, setSelectedLanguage] = useState(null);
    const [shouldScroll, setShouldScroll] = useState(false);

    const lang = useSelector((state) => state.ui.lang);
    const langList = useSelector((state) => state.ui.langList);

    // isOpen 값이 변경될 때마다 실행 (모달이 열릴 때)
    useEffect(() => {
        if (isOpen) {
            // 검색어 초기화
            setSearchTerm('');

            // 선택된 언어가 있으면 해당 위치로 스크롤
            if (selectedLanguage) {
                setShouldScroll(true);
            }
        }
    }, [isOpen, selectedLanguage]);

    // 선택된 언어로 스크롤 이동하는 useEffect
    useEffect(() => {
        if (selectedLanguage && selectedLangRef.current && shouldScroll) {
            selectedLangRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
            // 스크롤 완료 후 플래그 리셋
            setShouldScroll(false);
        }
    }, [selectedLanguage, shouldScroll]);

    // 초기화
    useEffect(() => {
        if (lang) {
            setSelectedLanguage(lang);
            setShouldScroll(true);
        }
    }, [lang]);

    const handleLanguageSelect = (language) => {
        if (selectedLanguage && selectedLanguage.locale === language.locale) {
            setSelectedLanguage(null);
        } else {
            setSelectedLanguage(language);
            // 언어 선택 후 스크롤 트리거
            setShouldScroll(true);
        }

        dispatch(setLang(language));
        changeLanguage(language);
        console.log("Selected language:", language);
    };

    // 검색어에 따라 필터링된 언어 목록 생성 - 정렬 유지
    const filteredLanguages = langList.length > 0 ? langList.filter((lang: any) =>
        lang.endonym?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (lang.localizedName && lang.localizedName.toLowerCase().includes(searchTerm.toLowerCase()))
    ) : [];

    return (
        <Modal
            open={isOpen}
            onClose={toggleModal}
            className="lang-modal-root"
            disablePortal
        >
            <Box className="lang-modal-box">
                <Box className="lang-modal-header">
                    <Typography variant="h6" className="lang-modal-title">
                        {t('header.language_modal.title') || '언어 선택'}
                    </Typography>
                    <IconButton
                        onClick={toggleModal}
                        className="lang-modal-close"
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Current Selected Language */}
                {selectedLanguage && (
                    <div className="lang-modal-current-selected">
                        <Box className="lang-modal-current-selected-inner">
                            <Box className="lang-modal-current-selected-label">
                                <LanguageIcon />
                                <Typography variant="subtitle1" className="lang-modal-current-selected-title">
                                    {t('header.language_modal.current_selected') || '현재 선택된 언어'}
                                </Typography>
                            </Box>
                            <Chip
                                label={
                                    <Box className="lang-modal-current-selected-chip-label">
                                        <Typography className="lang-modal-current-selected-chip-text">{selectedLanguage.endonym}</Typography>
                                    </Box>
                                }
                                className="lang-modal-current-selected-chip"
                            />
                        </Box>
                    </div>
                )}

                {/* Search Box */}
                <Box className="lang-modal-search-box"
                >
                    <Box className="lang-modal-search-inner"
                    >
                        <SearchIcon className="lang-modal-search-icon" />
                        <TextField
                            placeholder={t('header.language_modal.search_placeholder') || '언어 검색'}
                            fullWidth
                            variant="standard"
                            value={searchTerm} // 검색어 값 바인딩 추가
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                disableUnderline: true,
                            }}
                            className="lang-modal-search-input"
                        />
                    </Box>
                </Box>

                {/* Language List */}
                <Box className="lang-modal-list-box"
                >
                    <List className="lang-modal-list">
                        {filteredLanguages.length > 0 && filteredLanguages.map((flang) => (
                            <ListItem
                                key={flang.locale}
                                ref={selectedLanguage && selectedLanguage.locale === flang.locale ? selectedLangRef : null}
                                onClick={() => handleLanguageSelect(flang)}
                                className={
                                    `lang-modal-list-item${selectedLanguage && selectedLanguage.locale === flang.locale ? ' lang-modal-list-item--selected' : ''}`
                                }
                            >
                                <Box className="lang-modal-list-item-label">
                                    <Typography component="div" className="lang-modal-list-item-endonym">
                                        {flang.endonym}
                                    </Typography>
                                    <Typography component="div" className="lang-modal-list-item-localized">
                                        {flang.localizedName}
                                    </Typography>
                                </Box>
                                {selectedLanguage && selectedLanguage.locale === flang.locale && (
                                    <CheckIcon className="lang-modal-list-item-check" />
                                )}
                            </ListItem>
                        ))}
                    </List>
                </Box>
            </Box>
        </Modal>
    );
}

export default LangSelectModal;